import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import FieldError from "Shared/Form/FieldError";
import Input, { InputProps, inputStyle } from "Shared/Form/Input";
import Label from "Shared/Form/Label";
import { CanadianPostalCode, phone, required, USZipCode } from "Shared/Form/validators";
import { CircleRight } from "Shared/Icons";
import styled from "styled-components";
import FormBody from "src/components/ContinueDeal/CreditApp/shared/FormBody";
import FormContainer from "src/components/ContinueDeal/CreditApp/shared/FormContainer";
import FormHeader from "src/components/ContinueDeal/CreditApp/shared/FormHeader";
import * as variables from "src/styles/variables.scss";
import StepContent from "../../StepContent/StepContent";
import { BaseStepProps } from "../shared/BaseStepProps";
import { ASTERISK } from "src/constants";
import { Account } from "Types/api/Account";
import { getRegion } from "src/utils";
import { isUS } from "src/utils/account";
import PostalCode, { insertSpace, shouldInsertSpace } from "src/components/CustomizePayment/ZipCode/CAPostalCode";
import USPostalCode from "src/components/CustomizePayment/ZipCode/USZipCode";
import { employmentPageData, getEmploymentAddressFields } from "./employmentInfoPageFields";
import { Field } from "./Field";
import FieldGroup from "./FieldGroup";

const FormGroup = styled("div")`
    margin-bottom: 20px;

    input,
    label {
        display: block;
    }
`;

const ZipCode = styled(USPostalCode)<InputProps>`
    ${inputStyle};
    padding: 16px 5px;

    input {
        padding: 0;
    }

    ${(props) => props.theme.breakpoints.up("desktop")} {
        padding: 16px 0 16px 10px;
    }
`;

const CAPostalCode = styled(PostalCode)<InputProps>`
    ${inputStyle};
    padding: 16px 5px;

    input {
        padding: 0;
    }

    ${(props) => props.theme.breakpoints.up("desktop")} {
        padding: 16px 0 16px 10px;
    }
`;

const PostalCodeWrapper = styled.div`
    display: inline-block;
    vertical-align: top;
    width: 32%;
`;

const EmploymentInformationBody = styled(FormBody)`
    margin: 15px;

    ${(props) => props.theme.breakpoints.up("desktop")} {
        margin: 28px 41px 0 41px;
    }

    .EmploymentInfo {
        &__employer {
            margin-bottom: 10px;
        }

        &__phone-date-wrapper {
            ${(props) => props.theme.breakpoints.up("desktop")} {
                display: flex;
                justify-content: space-between;
                margin-bottom: 10px;
            }
        }

        &__prev-employer-note {
            font-weight: 500;
            margin-bottom: 30px;

            &-icon {
                color: ${(props) => props.theme.palette.success.main};
                height: 20px;
                margin-right: 8px;
                vertical-align: middle;
                width: 20px;
            }

            &-text {
                vertical-align: middle;
            }
        }
    }
`;
EmploymentInformationBody.displayName = "EmploymentInfoPageContent";

const StepContentContainer = styled("div")`
    margin: auto;

    ${(props) => props.theme.breakpoints.up("desktop")} {
        margin: 50px 22px 0;
    }
`;

const NameSmallText = styled("span")`
    color: ${variables.colorDark};
    display: flex;
    font-size: 12px;
    font-style: italic;
    font-weight: 300;
    justify-content: flex-end;
    margin-top: 3px;
    text-align: right;
`;

const TRANSLATION_KEY = "ContinueDeal.CreditApp.EmploymentInformation";

export interface EmploymentInfoFormData {
    currentEmployerName: string;
    startDate: Date | null;
    jobTitle: string;
    previousEmployerName: string;
    primaryWorkPhone: string;
    workAddress?: string;
    workCity?: string;
    workState?: string;
    workPostal?: string;
}

interface EmploymentInformationProps extends BaseStepProps {
    account?: Account;
    onNextClick: (data: EmploymentInfoFormData) => any;
}

type ErrorMessage = string;
type NoError = null;

type FormValues = {
    [key in keyof EmploymentInfoFormData]: any;
};

export default function EmploymentInformation({
    onBackClick,
    formData,
    isCosigner,
    formSettings,
    onNextClick,
    account,
}: EmploymentInformationProps) {
    const { t } = useTranslation();
    const region = getRegion(account);

    const initialState = {
        currentEmployerName: (formData && formData.currentEmployerName) ?? "",
        jobTitle: (formData && formData.jobTitle) ?? "",
        previousEmployerName: (formData && formData.previousEmployerName) ?? "",
        primaryWorkPhone: (formData && formData.primaryWorkPhone) ?? "",
        startDate: (formData && formData.startDate) ?? "",
    };

    const initialStateWithWorkAddress = {
        ...initialState,
        workAddress: (formData && formData.workAddress) ?? "",
        workCity: (formData && formData.workCity) ?? "",
        workState: (formData && formData.workState) ?? "",
        workPostal: (formData && formData.workPostal) ?? "",
    };

    const hasWorkAddressInSettings = formSettings && "primary_work_address" in formSettings;

    const [pageData, setPageData] = useState<
        Array<{
            name: string;
            id: string;
            label?: string;
            wrapper?: React.ReactNode;
            component?: React.ReactNode;
            inputProps?: {};
            fields?: any;
        }>
    >(employmentPageData);
    const [formValues, setFormValues] = useState<FormValues>(() => {
        return hasWorkAddressInSettings ? initialStateWithWorkAddress : initialState;
    });
    const [errors, setErrors] = useState<{ [field: string]: ErrorMessage | NoError }>({});
    const [showPreviousEmployer, setShowPreviousEmployer] = useState<boolean>(false);

    const handleBackClick = () => {
        onBackClick(formValues);
    };

    const handleNextClick = () => {
        if (isValid()) {
            onNextClick(formValues);
        }
    };

    const handleInputChange = useCallback(
        (e: { target: { value: string } }) => {
            const target = e.target as HTMLInputElement;
            const value: string = target.value;
            const name: string = target.name;
            setFormValues({ ...formValues, [name]: value });
        },
        [formValues]
    );

    const handleInputBlur = (e: { target: { value: string } }) => {
        const target = e.target as HTMLInputElement;
        const value: string = target.value;
        const name: string = target.name;

        if (name === "primaryWorkPhone") {
            setErrors({ primaryWorkPhone: required(value) + phone(value) });
        }
        if (name === "workPostal") {
            isUS(region)
                ? setErrors({ workPostal: required(value) + USZipCode(value) })
                : setErrors({ workPostal: required(value) + CanadianPostalCode(value) });
        } else {
            setErrors({ ...errors, [name]: required(value) });
        }
    };

    const handlePostalCodeChange = ({ target: { value } }: { target: { value: any } }) => {
        shouldInsertSpace(value)
            ? setFormValues({ ...formValues, workPostal: insertSpace(value).toUpperCase() })
            : setFormValues({ ...formValues, workPostal: value.toUpperCase() });
    };

    const isLessThanTwoYears = useCallback((today: Date, date: Date) => {
        const todayYear = today.getFullYear();
        const todayMonth = today.getMonth();
        const dateYear = date.getFullYear();
        const dateMonth = date.getMonth();
        return todayYear - dateYear < 2 || (todayYear - dateYear === 2 && todayMonth - dateMonth < 0);
    }, []);

    const isValid = useCallback((): boolean => {
        const error: { [index: string]: string } = {};
        error.currentEmployerName = required(formValues.currentEmployerName);
        error.jobTitle = required(formValues.jobTitle);
        error.primaryWorkPhone = required(formValues.primaryWorkPhone) + phone(formValues.primaryWorkPhone);
        error.startDate = required(formValues.startDate);

        if (hasWorkAddressInSettings) {
            error.workAddress = required(formValues.workAddress);
            error.workCity = required(formValues.workCity);
            error.workState = required(formValues.workState);
            error.workPostal = isUS(region)
                ? required(formValues.workPostal) + USZipCode(formValues.workPostal)
                : required(formValues.workPostal) + CanadianPostalCode(formValues.workPostal);
        }

        if (showPreviousEmployer) {
            error.previousEmployerName = required(formValues.previousEmployerName);
        }

        setErrors({ ...error });

        for (const key in error) {
            if (error[key]) {
                return false;
            }
        }
        return true;
    }, [formValues, hasWorkAddressInSettings, region, showPreviousEmployer]);

    useEffect(() => {
        formValues.startDate && setShowPreviousEmployer(isLessThanTwoYears(new Date(), formValues.startDate));
    }, [formValues.startDate, isLessThanTwoYears, setShowPreviousEmployer]);

    useEffect(() => {
        if (hasWorkAddressInSettings) {
            const addressFields = getEmploymentAddressFields(region);
            setPageData((currentData) => [...currentData, ...addressFields]);
        }
    }, [hasWorkAddressInSettings, region]);

    return (
        <FormContainer className="EmploymentInfo__container">
            <FormHeader className="EmploymentInfo__header">
                {isCosigner ? t(`${TRANSLATION_KEY}.cosignerTitle`) : t(`${TRANSLATION_KEY}.title`)}
            </FormHeader>
            <EmploymentInformationBody>
                <form>
                    <FormGroup>
                        {pageData.map((field: any) => {
                            if (field.component === "fieldSet") {
                                return (
                                    <FieldGroup
                                        key={field.id}
                                        field={field}
                                        values={formValues}
                                        onChange={handleInputChange}
                                        errors={errors}
                                        onBlur={handleInputBlur}
                                    />
                                );
                            }
                            if (field.name === "currentEmployerName") {
                                return (
                                    <Field
                                        key={field.id}
                                        field={field}
                                        onChange={handleInputChange}
                                        onBlur={handleInputBlur}
                                        value={formValues[field.name]}
                                        error={errors[field.name]}
                                    >
                                        <NameSmallText>{t(`${TRANSLATION_KEY}.employerHint`)}</NameSmallText>
                                    </Field>
                                );
                            }

                            if (field.name === "workPostal") {
                                return (
                                    <PostalCodeWrapper key={field.id}>
                                        <Label htmlFor={field.name}>{field.label}</Label>
                                        {isUS(region) ? (
                                            <ZipCode
                                                name={field.name}
                                                id={field.id}
                                                value={formValues[field.name]}
                                                onChange={handleInputChange}
                                                onBlur={handleInputBlur}
                                                hasError={!!errors.workPostal}
                                                hasLabel={true}
                                                error={!!errors.workPostal}
                                            />
                                        ) : (
                                            <CAPostalCode
                                                name={field.name}
                                                id={field.id}
                                                value={formValues[field.name]}
                                                onChange={handlePostalCodeChange}
                                                onBlur={handleInputBlur}
                                                hasError={!!errors.workPostal}
                                                hasLabel={true}
                                                error={!!errors.workPostal}
                                            />
                                        )}

                                        <FieldError>{errors.workPostal}</FieldError>
                                    </PostalCodeWrapper>
                                );
                            }

                            return (
                                <Field
                                    key={field.id}
                                    field={field}
                                    onChange={handleInputChange}
                                    value={formValues[field.name]}
                                    onBlur={handleInputBlur}
                                    error={errors[field.name]}
                                />
                            );
                        })}
                    </FormGroup>

                    {showPreviousEmployer ? (
                        <Fragment>
                            <div className="EmploymentInfo__prev-employer-note">
                                <CircleRight className="EmploymentInfo__prev-employer-note-icon" />
                                <span className="EmploymentInfo__prev-employer-note-text">
                                    {t("ContinueDeal.CreditApp.EmploymentInformation.previousEmployerMessage")}
                                </span>
                            </div>
                            <FormGroup className="EmploymentInfo__prev-employer">
                                <Label htmlFor="previousEmployerName">
                                    {t("ContinueDeal.CreditApp.EmploymentInformation.previousEmployerNameLabel")}
                                    {ASTERISK}
                                </Label>
                                <Input
                                    {...{
                                        error: !!errors.previousEmployerName,
                                        hasError: !!errors.previousEmployerName,
                                        hasLabel: true,
                                        id: "previousEmployerName",
                                        name: "previousEmployerName",
                                        onBlur: handleInputBlur,
                                        onChange: handleInputChange,
                                        value: formValues.previousEmployerName,
                                    }}
                                />
                                <FieldError>{errors.previousEmployerName}</FieldError>
                            </FormGroup>
                        </Fragment>
                    ) : null}
                </form>
            </EmploymentInformationBody>
            <StepContentContainer>
                <StepContent onBackClick={handleBackClick} onNextClick={handleNextClick} />
            </StepContentContainer>
        </FormContainer>
    );
}
